import videoUtils from "../custom/video-utils";

const SM = 576;
const LG = 992;

const parseVideo = (url) => {
	if (!url) {
		return null;
	}
	let match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
	if (match) {
		return { type: "vimeo", id: match[1] };
	}
	match = url.match(
		/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|embed)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
	);
	if (match) {
		return { type: "youtube", id: match[1] };
	}
	if (/\.(mp4|webm|ogg)(\?|$)/i.test(url) || url.indexOf("/progressive_redirect/") !== -1) {
		return { type: "html5", url };
	}
	return { type: "unknown", url };
};

const columnCount = () => {
	const width = window.innerWidth;
	if (width >= LG) {
		return 4;
	}
	if (width >= SM) {
		return 2;
	}
	return 1;
};

const prefersReducedMotion = () =>
	window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const mediaEl = (panel) => panel.querySelector(".ldc-eg__media--video");

const setMediaPlaying = (panel, isPlaying) => {
	const media = mediaEl(panel);
	if (media) {
		media.classList.toggle("is-playing", isPlaying);
	}
};

const pauseInline = async (panel) => {
	if (!panel) {
		return;
	}
	const player = panel.querySelector(".ldc-eg__player");
	if (!player) {
		return;
	}
	const video = player.querySelector("video");
	const iframe = player.querySelector("iframe");
	if (video) {
		video.pause();
	}
	if (iframe) {
		if (iframe.src.includes("vimeo.com")) {
			try {
				await videoUtils.pauseVimeo(iframe);
			} catch (e) {
				/* ignore */
			}
		} else if (iframe.src.includes("youtube.com")) {
			try {
				await videoUtils.pauseYouTube(iframe);
			} catch (e) {
				/* ignore */
			}
		}
	}
	player.innerHTML = "";
	player.setAttribute("hidden", "");
	setMediaPlaying(panel, false);
};

const injectInlinePlayer = async (panel) => {
	const url = panel.getAttribute("data-eg-video") || "";
	const parsed = parseVideo(url);
	const player = panel.querySelector(".ldc-eg__player");
	if (!parsed || !player) {
		return;
	}

	player.removeAttribute("hidden");
	player.innerHTML = "";

	const id = `ldc-eg-player-${Math.random().toString(36).slice(2, 9)}`;

	if (parsed.type === "vimeo") {
		const iframe = document.createElement("iframe");
		iframe.id = id;
		iframe.className = "ldc-eg__iframe";
		iframe.setAttribute("loading", "lazy");
		iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
		iframe.setAttribute("allowfullscreen", "");
		iframe.src = `https://player.vimeo.com/video/${parsed.id}?autoplay=1&controls=1`;
		player.appendChild(iframe);
		try {
			await videoUtils.loadVimeoAPI();
			await videoUtils.playAndControlVimeo(iframe);
		} catch (e) {
			/* iframe may still play */
		}
		setMediaPlaying(panel, true);
		return;
	}

	if (parsed.type === "youtube") {
		const iframe = document.createElement("iframe");
		iframe.id = id;
		iframe.className = "ldc-eg__iframe";
		iframe.setAttribute("loading", "lazy");
		iframe.setAttribute("allow", "accelerometer; autoplay; fullscreen; picture-in-picture");
		iframe.setAttribute("allowfullscreen", "");
		iframe.src = `https://www.youtube.com/embed/${parsed.id}?autoplay=1&enablejsapi=1`;
		player.appendChild(iframe);
		try {
			await videoUtils.playYouTube(iframe);
		} catch (e) {
			/* ignore */
		}
		setMediaPlaying(panel, true);
		return;
	}

	if (parsed.type === "html5") {
		const video = document.createElement("video");
		video.id = id;
		video.className = "ldc-eg__html5";
		video.setAttribute("playsinline", "");
		video.setAttribute("controls", "");
		video.src = parsed.url;
		player.appendChild(video);
		try {
			await video.play();
		} catch (e) {
			/* autoplay blocked */
		}
		setMediaPlaying(panel, true);
	}
};

const openLightbox = (panel) => {
	const link = panel.querySelector("a.ldc-eg__lightbox");
	if (link) {
		link.click();
		return;
	}
	const url = panel.getAttribute("data-eg-video");
	if (url && window.jQuery && window.jQuery.fancybox) {
		window.jQuery.fancybox.open({ src: url, type: "iframe" });
	}
};

const expandableGrid = (root) => {
	const grid = root.querySelector(".ldc-eg__grid");
	if (!grid) {
		return;
	}

	let openPanel = null;
	const fillers = [];

	const cells = () => Array.from(grid.querySelectorAll(".ldc-eg__cell"));
	const panels = () => Array.from(grid.querySelectorAll(".ldc-eg__panel"));

	const syncFillers = () => {
		const cols = columnCount();
		const count = cells().length;
		const remainder = count % cols;
		const needed = remainder === 0 ? 0 : cols - remainder;
		while (fillers.length > needed) {
			const node = fillers.pop();
			node.remove();
		}
		while (fillers.length < needed) {
			const filler = document.createElement("div");
			filler.className = "ldc-eg__filler";
			filler.setAttribute("aria-hidden", "true");
			grid.appendChild(filler);
			fillers.push(filler);
		}
		fillers.forEach((filler, i) => {
			filler.style.order = String((count + i) * 10);
		});
		if (openPanel) {
			placePanel(openPanel);
		}
	};

	const placePanel = (panel) => {
		const cols = columnCount();
		const index = Number(panel.getAttribute("data-eg-index") || 0);
		const rowStart = Math.floor(index / cols) * cols;
		const panelOrder = (rowStart + cols) * 10 - 5;
		panel.style.order = String(panelOrder);
	};

	const setExpanded = (panel, expanded) => {
		const index = panel.getAttribute("data-eg-index");
		const cell = grid.querySelector(`.ldc-eg__cell[data-eg-index="${index}"]`);
		const button = cell ? cell.querySelector(".ldc-eg__expand") : null;
		if (button) {
			button.setAttribute("aria-expanded", expanded ? "true" : "false");
		}
		if (cell) {
			cell.classList.toggle("is-open", expanded);
		}
		panel.hidden = !expanded;
		panel.classList.toggle("is-open", expanded);
	};

	const closePanel = async (panel) => {
		if (!panel || panel.hidden) {
			return;
		}
		await pauseInline(panel);
		setExpanded(panel, false);
		if (openPanel === panel) {
			openPanel = null;
		}
	};

	const openPanelAt = async (panel, { playInline = false } = {}) => {
		if (openPanel && openPanel !== panel) {
			await closePanel(openPanel);
		}
		placePanel(panel);
		setExpanded(panel, true);
		openPanel = panel;
		if (!prefersReducedMotion()) {
			panel.scrollIntoView({ block: "nearest", behavior: "smooth" });
		}
		if (playInline && panel.getAttribute("data-eg-play-mode") === "inline") {
			await injectInlinePlayer(panel);
		}
	};

	const togglePanel = async (panel) => {
		if (!panel.hidden) {
			await closePanel(panel);
			return;
		}
		await openPanelAt(panel);
	};

	root.addEventListener("click", async (event) => {
		const watch = event.target.closest("[data-eg-watch]");
		if (watch && root.contains(watch)) {
			event.preventDefault();
			event.stopPropagation();
			const cell = watch.closest(".ldc-eg__cell");
			const index = cell && cell.getAttribute("data-eg-index");
			const panel = index != null ? grid.querySelector(`.ldc-eg__panel[data-eg-index="${index}"]`) : null;
			if (!panel) {
				return;
			}
			const mode = watch.getAttribute("data-eg-play-mode") || panel.getAttribute("data-eg-play-mode");
			if (mode === "inline") {
				await openPanelAt(panel, { playInline: true });
			} else {
				openLightbox(panel);
			}
			return;
		}

		const inlinePlay = event.target.closest("[data-eg-inline-play]");
		if (inlinePlay && root.contains(inlinePlay)) {
			event.preventDefault();
			const panel = inlinePlay.closest(".ldc-eg__panel");
			if (!panel) {
				return;
			}
			const player = panel.querySelector(".ldc-eg__player");
			if (player && !player.hasAttribute("hidden") && player.innerHTML.trim() !== "") {
				await pauseInline(panel);
				return;
			}
			await injectInlinePlayer(panel);
			return;
		}

		const closeBtn = event.target.closest("[data-eg-close]");
		if (closeBtn && root.contains(closeBtn)) {
			event.preventDefault();
			const panel = closeBtn.closest(".ldc-eg__panel");
			if (!panel) {
				return;
			}
			const index = panel.getAttribute("data-eg-index");
			await closePanel(panel);
			const cell = index != null ? grid.querySelector(`.ldc-eg__cell[data-eg-index="${index}"]`) : null;
			const expand = cell ? cell.querySelector(".ldc-eg__expand") : null;
			if (expand) {
				expand.focus();
			}
			return;
		}

		const cell = event.target.closest(".ldc-eg__cell");
		if (cell && root.contains(cell)) {
			const index = cell.getAttribute("data-eg-index");
			const panel = index != null ? grid.querySelector(`.ldc-eg__panel[data-eg-index="${index}"]`) : null;
			if (panel) {
				await togglePanel(panel);
			}
		}
	});

	document.addEventListener("keydown", async (event) => {
		if (event.key === "Escape" && openPanel) {
			await closePanel(openPanel);
		}
	});

	let resizeTimer = 0;
	window.addEventListener("resize", () => {
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(() => {
			syncFillers();
		}, 150);
	});

	syncFillers();
};

const init = () => {
	document.querySelectorAll(".block-expandable-grid").forEach((block) => {
		if (block.dataset.egReady) {
			return;
		}
		block.dataset.egReady = "1";
		expandableGrid(block);
	});
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
